import React, { useContext, useEffect, useState } from "react";
import { PageHeader } from "@ant-design/pro-layout";
import { Card, Table, Button, Tag, Space } from "antd";
import {
  HomeOutlined,
  DatabaseOutlined,
  SwapOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { getStockTransfers } from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import moment from "moment";
import {
  InventoryRequestStatus,
  UserType,
} from "../../../configs/constants";
import { useNavigate } from "react-router-dom";
import DirectTransfer from "./DirectTransfer";
import RequestTransfer from "./RequestTransfer";
import { handleApiError } from "../../../utils/errorHandler";

const StockTransfer = () => {
  const { authUser } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [stockTransferData, setStockTransferData] = useState([]);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const { t } = useTranslation();
  let navigate = useNavigate();

  useEffect(() => {
    setIsDataLoading(true);
    getStockTransfers({
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const responseData = response?.data?.data || {};
        const stockTransfers = responseData.stock_transfers;

        setStockTransferData(
          Array.isArray(stockTransfers) ? stockTransfers : [],
        );
        setPaginationData(responseData.pagination || {});
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, currentPage, perPageSize, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/inventory/stock",
      breadcrumbName: t("inventory"),
      icon: <DatabaseOutlined />,
    },
    {
      path: "inventory/stock-transfer",
      breadcrumbName: t("stock_transfer"),
      icon: <SwapOutlined />,
    },
  ];

  const tableColumns = [
    {
      title: t("date"),
      dataIndex: "date_time",
      render: (date_time) => {
        return date_time
          ? moment(date_time).format("MMM. D, YYYY hh:mm a")
          : "-";
      },
    },
    {
      title: t("requested_quantity"),
      align: "center",
      dataIndex: "total_requested_quantity",
    },
    {
      title: t("approved_quantity"),
      align: "center",
      dataIndex: "total_approved_quantity",
    },
    {
      title: t("branch_from"),
      dataIndex: "branch_from",
      render: (branch_from) => {
        return branch_from?.name || "-";
      },
    },
    {
      title: t("branch_to"),
      dataIndex: "branch_to",
      render: (branch_to) => {
        return branch_to?.name || "-";
      },
    },
    {
      title: t("status"),
      render: (record) => {
        return (
          <>
            {record.status === InventoryRequestStatus.Pending && (
              <Tag color="orange">{t("pending")}</Tag>
            )}
            {record.status === InventoryRequestStatus.Completed && (
              <Tag color="green">{t("completed")}</Tag>
            )}
            {record.status === InventoryRequestStatus.Cancelled && (
              <Tag color="red">{t("cancelled")}</Tag>
            )}
            {record.status === InventoryRequestStatus.Rejected && (
              <Tag color="red">{t("rejected")}</Tag>
            )}
          </>
        );
      },
    },
    {
      title: t("created_by"),
      render: (record) => {
        return <>{record?.created_by?.name || "-"}</>;
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => {
                navigate("/inventory/stock-transfer/" + record.id);
              }}
            >
              <EyeOutlined title={t("view_details")} />
            </Button>
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("stock_transfer")}
          ghost={false}
          extra={[
            (authUser.can_request_stock_transfer ||
              authUser.type === UserType.BusinessOwner) && (
              <RequestTransfer
                reloadCallback={() => setReloadTableData(!reloadTableData)}
                key="request"
              />
            ),
            authUser.type === UserType.BusinessOwner && (
              <DirectTransfer
                reloadCallback={() => setReloadTableData(!reloadTableData)}
                key="direct"
              />
            ),
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={tableColumns}
          dataSource={stockTransferData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

export default StockTransfer;
